<template>
  <BoxWithTitle>
    <RedirectModal
      :show="showRedirectModal"
      @close="showRedirectModal = false"
    />
    <template #header>
      <v-toolbar-title>
        {{ $t('riskAnalysis.pageName') }}
      </v-toolbar-title>
    </template>

    <template #icons>
      <v-btn
        :disabled="!risk.editable || (!hasPermission('riskAnalyses.add') && hasPermission('riskAnalyses.edit'))"
        text
        @click="onClickSave(false)"
      >
        {{ $t('forms.btn.saveAsDraft') }}
      </v-btn>
      <v-btn
        :disabled="!risk.editable || !hasPermission('riskAnalyses.add')"
        text
        @click="onClickSave(true)"
      >
        {{ $t('forms.btn.save') }}
      </v-btn>
    </template>

    <template #content>
      <component
        :is="currentComponent"
        v-bind="risk"
        @sendData="data = $event"
      />

      <v-row
        align="center"
        justify="end"
        class="mr-1"
      >
        <v-btn
          v-if="$route.params.riskAnalysisId && hasPermission('riskAnalyses.remove')"
          text
          @click="onClickConfirmRemove"
        >
          <span class="text-danger">
            {{ $t('forms.btn.delete') }}
          </span>
        </v-btn>
      </v-row>
    </template>
  </BoxWithTitle>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle.vue';
import RiskEvaluationClosedNetworkForm from '@/components/sites/riskAssessments/RiskAssessmentClosedNetworkForm.vue';
import RiskAssessmentSteamNetworkForm from '@/components/sites/riskAssessments/RiskAssessmentSteamNetworkForm.vue';
import RiskEvaluationCoolingNetworkForm from '@/components/sites/riskAssessments/RiskAssessmentCoolingNetworkForm.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import { isEmpty } from 'lodash';
import RedirectModal from '@/components/shared/RedirectModal.vue';
// eslint-disable-next-line import/no-cycle
import lockService from '@/utilities/lockService';

export default {
    components: {
        BoxWithTitle,
        RedirectModal,
    },
    data() {
        return {
            showRedirectModal: false,
            data: {},
            currentComponent: null,
            risk: null,
            actionName: 'riskAnalysisComponent',
        };
    },
    computed: {
        ...mapGetters([
            'network',
            'component',
            'riskAnalysis',
            'hasPermission',
        ]),
    },
    mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },
    async beforeRouteLeave(to, from, next) {
        try {
            if (this.component && this.component.id) {
                const releaseStatus = await lockService.releaseLock(this.component.id, this.actionName);
                if (!releaseStatus) return next(false);
            }
        } catch (error) {
            console.error('Error releasing lock on route leave:', error);
            // Continue navigation even if lock release fails
        }
        return next();
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleUnload);
        // Ensure locks are released even if beforeRouteLeave didn't complete
        if (this.component && this.component.id) {
            lockService.releaseLock(this.component.id, this.actionName)
                .catch(err => console.error('Error releasing lock on destroy:', err));
        }
    },
    async created() {
        this.getData();
        if (!this.$store.getters.lockFrom) {
            await this.checkLockOnLoad();
        }
    },
    methods: {
        ...mapActions({
            reloadRiskAnalysis: 'getRiskAnalysisInfo',
            resetRiskAnalysis: 'resetRiskAnalysisInfo',
        }),
        handleUnload() {
            if (this.component && this.component.id) {
                // Handle synchronously for page unload events
                try {
                    lockService.releaseLock(this.component.id, this.actionName);
                } catch (error) {
                    console.error('Error releasing lock on unload:', error);
                }
            }
        },
        async checkLockOnLoad() {
            try {
                const lockAvailable = await lockService.getLockStatus(this.component.id, this.actionName);
                if (!lockAvailable) {
                    this.showRedirectModal = true;
                    return;
                }
                await lockService.requestLock(this.component.id, this.actionName);
            } catch (error) {
                this.showRedirectModal = true;
            }
        },
        getData() {
            this.risk = {
                network: this.network.id,
                editable: true,
                name: this.network.name,
                type: this.$t(`networkTypes.${this.network.type.key}`),
            };
            if (!isEmpty(this.component)) {
                this.risk.component = this.component.id;
                this.risk.componentName = this.component.name;
                this.risk.componentType = this.component.type.name;
            }

            switch (this.network.type.key) {
                case 'cooling':
                    this.currentComponent = Vue.component('RiskEvaluationCoolingNetworkForm', RiskEvaluationCoolingNetworkForm);
                    break;
                case 'steam':
                    this.currentComponent = Vue.component('RiskAssessmentSteamNetworkForm', RiskAssessmentSteamNetworkForm);
                    break;
                case 'closed':
                    this.currentComponent = Vue.component('RiskEvaluationClosedNetworkForm', RiskEvaluationClosedNetworkForm);
                    break;
                default:
                    this.currentComponent = null;
            }

            if (this.riskAnalysis && this.riskAnalysis.id) {
                const { finished, generatedData, sections } = this.riskAnalysis;

                this.risk = {
                    ...this.risk,
                    editable: !finished,
                    sections: generatedData
                        ? generatedData.data.sections
                        : sections,
                };
            }
        },
        onClickSave(save) {
            if (this.riskAnalysis.id) {
                this.updateRequest(save);
            } else {
                this.addRequest(save);
            }
        },
        async addRequest(save) {
            this.data.save = save;
            await this.$rq.addRiskAnalysis(this.data);
            this.$toasted.global.toast_success({
                message: this.$t('msg.riskAnalysisCreated', { name: this.risk.name }),
            });
            this.goBack();
        },
        async updateRequest(save) {
            this.data.save = save;

            await this.$rq.updateRiskAnalysis(this.$route.params.riskAnalysisId, this.data);
            await this.reloadRiskAnalysis(this.riskAnalysis.id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.riskAnalysisModified', { name: this.risk.name }),
            });
            this.goBack();
        },
        async removeRequest() {
            await this.$rq.deleteRiskAnalysis(this.riskAnalysis.id);
            this.resetRiskAnalysis();
            this.$toasted.global.toast_success({
                message: this.$t('msg.riskAnalysisRemoved', { name: this.risk.name }),
            });
            this.goBack();
        },
        onClickConfirmRemove() {
            this.$root.$emit('showModal', {
                title: this.$t('riskAnalysis.removeRiskAnalysis'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('riskAnalysis.confirmRemove') },
                onConfirm: () => this.removeRequest(),
                isForm: false,
            });
        },
        goBack() {
            this.$router.go(-1);
        },
    },
};
</script>
